import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, RefreshCw, Activity, ArrowUpRight, ClipboardCheck, FlaskConical, PieChart } from "lucide-react";
import api from "../../lib/api";

const DAYS_OPTIONS = [7, 30, 90];

const PURPOSE_LABEL = {
  agent_turn: "Chat (agent turns)",
  doc_extraction: "Document extraction",
  screening: "Sanctions / PEP screening",
  standup: "Daily ops standup",
  other: "Other",
};

const PURPOSE_COLOR = {
  agent_turn: "#00E5FF",
  doc_extraction: "#7CE7B7",
  screening: "#F2A65A",
  standup: "#B97AFF",
  other: "#7B7E89",
};

function fmtUsd(v) {
  return v === 0 ? "$0.00" : `$${(v || 0).toFixed(2)}`;
}

function fmtTokens(t) {
  if (t == null) return "0";
  if (t > 1_000_000) return `${(t / 1_000_000).toFixed(2)}M`;
  if (t > 1_000) return `${(t / 1_000).toFixed(1)}k`;
  return String(t);
}

function timeSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function freshnessTone(iso) {
  if (!iso) return { color: "text-zinc-500", dot: "bg-zinc-500", label: "no calls yet" };
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 30) return { color: "text-emerald-300", dot: "bg-emerald-400" };
  if (m < 24 * 60) return { color: "text-amber-300", dot: "bg-amber-400" };
  return { color: "text-zinc-400", dot: "bg-zinc-500" };
}


function EvalTile({ icon, label, run, testId }) {
  if (!run) {
    return (
      <div
        data-testid={testId}
        className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4"
      >
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          {icon}{label}
        </div>
        <div className="mt-2 text-[20px] text-zinc-600">— no runs yet —</div>
        <div className="mt-1 text-[11px] text-zinc-600">
          {label.includes("Real-model")
            ? "Run via: cd /app/backend && python -m eval.real_model.run_charter_eval"
            : "Stub runs are written by pytest test_nerea_charter_v1.py"}
        </div>
      </div>
    );
  }
  const rate = run.pass_rate != null ? run.pass_rate : (
    run.total_attacks ? run.pass_count / run.total_attacks : 0
  );
  const pct = (rate * 100).toFixed(1);
  const tone = rate >= 0.9 ? "text-emerald-300"
             : rate >= 0.75 ? "text-amber-300"
             : "text-rose-300";
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
        {icon}{label}
      </div>
      <div className={`mt-2 text-[24px] tracking-tight ${tone}`}>{pct}%</div>
      <div className="mt-1 text-[11.5px] text-zinc-400">
        {run.pass_count} / {run.total_attacks || (run.pass_count + run.fail_count)} attacks refused
      </div>
      <div className="mt-2 text-[10.5px] text-zinc-600">
        charter {run.charter_version} · {timeSince(run.finished_at || run.started_at)}
      </div>
    </div>
  );
}


function ServedByMixTile({ mix, testId }) {
  const buckets = mix?.buckets || [];
  const total = mix?.total_calls || 0;
  const top = buckets.slice(0, 4);
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
        <PieChart className="h-3.5 w-3.5" />Served-by mix (7d)
      </div>
      {total === 0 ? (
        <div className="mt-2 text-[18px] text-zinc-600">— no traffic —</div>
      ) : (
        <>
          <div className="mt-2 text-[20px] text-zinc-200">
            {total.toLocaleString()}<span className="text-[12px] text-zinc-500"> turns</span>
          </div>
          <div className="mt-2 space-y-1">
            {top.map((b) => {
              const tone = b.served_by === "platform_knowledge" ? "text-emerald-300"
                         : b.served_by === "paytech_faq" ? "text-cyan-300"
                         : b.served_by === "brain_abstain" ? "text-amber-300"
                         : b.served_by === "brain_rag" ? "text-zinc-300"
                         : "text-zinc-500";
              return (
                <div
                  key={b.served_by}
                  className="flex items-center justify-between text-[11.5px]"
                  data-testid={`served-by-${b.served_by}`}
                >
                  <span className={tone}>{b.served_by}</span>
                  <span className="text-zinc-400">{(b.share * 100).toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


function Sparkline({ series }) {
  if (!series || series.length < 2) {
    return <div className="text-zinc-600 text-[11.5px]">— not enough data —</div>;
  }
  const max = Math.max(...series.map((p) => p.spend_usd), 0.01);
  const w = 320;
  const h = 60;
  const stepX = series.length > 1 ? w / (series.length - 1) : 0;
  const points = series.map((p, i) => {
    const x = i * stepX;
    const y = h - (p.spend_usd / max) * (h - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="text-[#00E5FF]" data-testid="usage-sparkline">
      <polyline
        fill="none" stroke="currentColor" strokeWidth="1.5" points={points}
      />
      {series.map((p, i) => (
        <circle
          key={i}
          cx={i * stepX} cy={h - (p.spend_usd / max) * (h - 6) - 3}
          r="1.5" fill="currentColor" opacity={p.spend_usd > 0 ? 0.95 : 0.25}
        />
      ))}
    </svg>
  );
}


export default function AdminNereaUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  // Brief 3.6 Phase D Part 2 — migrated eval tiles from the deleted
  // /admin/nerea/eval page. Both fetched once on mount.
  const [stubRun, setStubRun] = useState(null);
  const [realRun, setRealRun] = useState(null);
  const [mix, setMix] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [stub, real, srvBy] = await Promise.all([
          api.get(`/admin/nerea/eval/runs?mode=stub&limit=1`).then((r) => r.data),
          api.get(`/admin/nerea/eval/runs?mode=real_model&limit=1`).then((r) => r.data),
          api.get(`/admin/nerea/served-by-mix?days=7`).then((r) => r.data),
        ]);
        if (cancelled) return;
        setStubRun((stub.rows || [])[0] || null);
        setRealRun((real.rows || [])[0] || null);
        setMix(srvBy || null);
      } catch {
        // Tiles fail soft — usage page should still render even if eval
        // tiles 404 in a fresh environment with no runs yet.
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get(`/admin/nerea/usage?days=${days}&top_n=10`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load usage.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, reloadTick]);

  const purposeRows = useMemo(() => {
    if (!data?.by_purpose) return [];
    return [...data.by_purpose].sort((a, b) => b.spend_usd - a.spend_usd);
  }, [data]);

  // Tick every 30 s so the "X minutes ago" string stays accurate without a refetch.
  const [, setNow] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const lastCall = data?.last_call;
  const freshness = freshnessTone(lastCall?.created_at);
  const freshnessText = lastCall?.created_at
    ? `Last call recorded ${timeSince(lastCall.created_at)}`
    : "No calls recorded yet";

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <DollarSign className="h-3 w-3 text-[#00E5FF]" /> Nerea
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        API spend
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        Last {days} days of Nerea API usage — totals, breakdown by purpose,
        and the merchants generating the most spend. Source:
        <code className="ml-1 text-zinc-500">nerea_usage</code>.
      </p>

      <div className="mt-6 flex items-center gap-2">
        {DAYS_OPTIONS.map((d) => (
          <button
            key={d}
            data-testid={`usage-window-${d}`}
            onClick={() => setDays(d)}
            className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors ${
              days === d
                ? "bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]"
                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
            }`}
          >
            {d} days
          </button>
        ))}
        <button
          data-testid="usage-reload"
          onClick={() => setReloadTick((t) => t + 1)}
          className="ml-2 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05] transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Reload
        </button>
        <div
          data-testid="usage-freshness"
          className={`ml-auto inline-flex items-center gap-2 text-[11.5px] ${freshness.color}`}
          title={lastCall?.created_at || ""}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${freshness.dot} ${lastCall?.created_at ? "animate-pulse" : ""}`} />
          {freshnessText}
        </div>
      </div>

      {err && (
        <div data-testid="usage-error" className="mt-4 rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-3 py-2 text-[12.5px] text-rose-200">
          {err}
        </div>
      )}

      {/* Brief 3.6 Phase D Part 2 — eval + served-by-mix tiles
          migrated here from the deleted /admin/nerea/eval page. */}
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="usage-charter-tiles">
        <EvalTile
          icon={<ClipboardCheck className="h-3.5 w-3.5" />}
          label="Stub eval — last run"
          run={stubRun}
          testId="usage-tile-stub-eval"
        />
        <EvalTile
          icon={<FlaskConical className="h-3.5 w-3.5" />}
          label="Real-model eval — last run"
          run={realRun}
          testId="usage-tile-real-eval"
        />
        <ServedByMixTile mix={mix} testId="usage-tile-served-by-mix" />
      </div>

      {/* Totals */}
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="usage-totals">
        <KpiCard label="Total spend" value={fmtUsd(data?.totals?.spend_usd || 0)} icon={<DollarSign className="h-3.5 w-3.5" />} testId="usage-kpi-spend" />
        <KpiCard label="API calls" value={(data?.totals?.calls || 0).toLocaleString()} icon={<Activity className="h-3.5 w-3.5" />} testId="usage-kpi-calls" />
        <KpiCard label="Tokens in" value={fmtTokens(data?.totals?.tokens_in)} testId="usage-kpi-tokens-in" />
        <KpiCard label="Tokens out" value={fmtTokens(data?.totals?.tokens_out)} testId="usage-kpi-tokens-out" />
      </div>

      {/* Spend trend sparkline */}
      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— Daily spend</div>
        <div className="mt-4">
          <Sparkline series={data?.daily_series || []} />
        </div>
      </div>

      {/* Breakdown by purpose */}
      <div className="mt-7">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Breakdown by purpose</div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          <table className="w-full text-[13px]" data-testid="usage-purpose-table">
            <thead>
              <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
                <th className="px-4 py-2.5">Purpose</th>
                <th className="px-4 py-2.5 text-right">Spend</th>
                <th className="px-4 py-2.5 text-right">Tokens</th>
                <th className="px-4 py-2.5 text-right">Calls</th>
              </tr>
            </thead>
            <tbody>
              {purposeRows.map((r) => (
                <tr key={r.purpose} className="border-t border-white/[0.05] text-zinc-300" data-testid={`usage-purpose-${r.purpose}`}>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: PURPOSE_COLOR[r.purpose] || "#7B7E89" }} />
                      {PURPOSE_LABEL[r.purpose] || r.purpose}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmtUsd(r.spend_usd)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{fmtTokens(r.tokens)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{(r.calls || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top 10 merchants */}
      <div className="mt-7">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Top 10 merchants by spend</div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          <table className="w-full text-[13px]" data-testid="usage-top-merchants-table">
            <thead>
              <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
                <th className="px-4 py-2.5">Merchant</th>
                <th className="px-4 py-2.5">Ref</th>
                <th className="px-4 py-2.5 text-right">Spend</th>
                <th className="px-4 py-2.5 text-right">Tokens</th>
                <th className="px-4 py-2.5 text-right">Calls</th>
                <th className="px-4 py-2.5">Last call</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.top_merchants || []).length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500" data-testid="usage-top-merchants-empty">
                    No merchant-attributed usage in this window.
                  </td>
                </tr>
              )}
              {(data?.top_merchants || []).map((m) => (
                <tr key={m.merchant_id} className="border-t border-white/[0.05] text-zinc-300" data-testid={`usage-merchant-${m.merchant_id}`}>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/admin/nerea/usage/merchants/${m.merchant_id}`}
                      data-testid={`usage-merchant-link-${m.merchant_id}`}
                      className="hover:text-[#00E5FF] transition-colors"
                    >
                      {m.legal_name || <span className="text-zinc-500">(unnamed)</span>}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-400">{m.ref || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmtUsd(m.spend_usd)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{fmtTokens(m.tokens)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{(m.calls || 0).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-zinc-400 font-mono text-[11.5px]">{(m.last_call_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/admin/nerea/usage/merchants/${m.merchant_id}`}
                      className="inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-[#00E5FF]"
                    >
                      Detail <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function KpiCard({ label, value, icon, testId }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-2xl text-white">{value}</div>
    </div>
  );
}
